import React, { useEffect, useState, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import './style.scss';
import {
  Table, Select, notification, Popconfirm, Button,
} from 'antd';
import {
  DeleteOutlined, PlusCircleFilled,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import Helpers from '@Apis/helpers';
import TenantActions from '@Actions/tenantActions';
import TallyIntegrationActions from '@Actions/integrations/tallyIntegrationActions';
import PRZSelect from '../../../../../../Common/UI/PRZSelect';

const { Option } = Select;

/**
 *
 */
const TallyMapLocations = ({
  user, getTenants, getTenantsLoading, tenants, getTallyConnections, tallyConnections, connectTenantToTallyLocation,
  connectTenantToTallyLocationLoading, setStep, getTallyConnectionsLoading, stepNumber,
}) => {
  const [tenantData, setTenantData] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [formSubmitted, setFormSubmitted] = useState('');
  const integrationTallyIdURL = Number(Helpers.getValueFromUrl('it_id'));

  useEffect(() => {
    getTenants(1, '', true, user?.tenant_info?.org_id);
    getTallyConnections(user?.tenant_info?.org_id);
  }, []);

  const tallyConnectionsFilteredData = tallyConnections?.filter((connection) => connection?.it_id === integrationTallyIdURL);

  useEffect(() => {
    const copyTenantData = tallyConnectionsFilteredData?.[0]?.tenant?.filter((item) => item?.tally_company_name)?.map((tenant) => ({ tenant_id: tenant?.tenant_id, tally_location_name: tenant?.tally_location_name, key: uuidv4() }));
    setTenantData(copyTenantData);
  }, [tenants]);

  const updateTableValue = (key, value, label) => {
    const data = tenantData.map((tenant) => {
      if (tenant.key === key) {
        // eslint-disable-next-line no-param-reassign
        tenant[label] = value;
        return tenant;
      }
      return tenant;
    });
    setTenantData(data);
  };

  const getSelectedTenant = (record) => tenantData?.filter((item) => !item?.is_deleted).filter((item) => item?.tenant_id !== record?.tenant_id).map((tenant) => tenant.tenant_id);

  const getDataSource = (data) => (data ? JSON.parse(JSON.stringify(data?.filter((tenant) => !tenant.is_deleted))) : []);

  const addNewRow = () => {
    if (!connectTenantToTallyLocationLoading) {
      if (tenantData?.length) {
        setTenantData([...tenantData, { key: uuidv4() }]);
      } else {
        setTenantData([{}]);
      }
    }
  };

  const removeRow = (key) => {
    if (tenantData?.filter((tenant) => !tenant.is_deleted)?.length === 1) {
      const newTenantData = tenantData?.map((tenant) => {
        if (tenant?.key === key) {
          return { ...tenant, is_deleted: true };
        }

        return { ...tenant };
      });
      setTenantData([...newTenantData, { key: uuidv4() }]);
    } else {
      const newTenantData = tenantData?.map((tenant) => {
        if (tenant?.key === key) {
          return { ...tenant, is_deleted: true };
        }

        return { ...tenant };
      });

      setTenantData(newTenantData);
    }
  };
  const columns = [
    {
      title: 'Business Unit Name',
      width: '312px',
      render: (record) => (
        <div>
          <PRZSelect
            value={record?.tenant_id}
            showSearch
            filterOption={(input, option) =>
              option?.children.toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => {
              const orgId = tenants?.[0]?.org_id;
              updateTableValue(record?.key, value, 'tenant_id');
              updateTableValue(record?.key, orgId, 'org_id');
              setSearchKeyword('');
            }}
            placeholder="Select Business Unit"
            style={{
              border: !(formSubmitted && !record?.tenant_id) ? '1px solid rgba(68, 130, 218, 0.2)' : '1px solid red',
              width: '100%',
              marginRight: '10px',
            }}
            onSearch={(value) => {
              setSearchKeyword(value);
            }}
            disabled={connectTenantToTallyLocationLoading}
          >
            {
              tallyConnectionsFilteredData && tallyConnectionsFilteredData.filter((connection) => connection?.company_details && connection?.locations)?.[0]?.tenant
                // Include the selected tenant based on record?.tenant_id, regardless of its it_id value
                ?.map((item) => {
                  // Check if item is the selected tenant or if it meets the new filter criteria (it_id === null)
                  const isSelectedTenant = item.tenant_id === record?.tenant_id;
                  const isItIdNull = item.it_id === null;

                  // Return the item if it's the selected tenant or meets the filter criteria
                  if (isSelectedTenant || isItIdNull) {
                    return (
                      <Option key={item.tenant_id} value={item.tenant_id}>
                        {item.tenant_name}
                      </Option>
                    );
                  }
                  return null; // Otherwise, do not include this tenant in the options
                }).filter(Boolean) // Remove any null entries from the map operation
            }
          </PRZSelect>
        </div>
      ),
    },
    {
      title: 'Location',
      width: '275px',
      render: (record) => (
        <div>
          <PRZSelect
            value={record.tally_location_name}
            showSearch
            filterOption={(input, option) =>
              option?.children.toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => {
              updateTableValue(record?.key, value, 'tally_location_name');
            }}
            placeholder="Select a location"
            style={{
              border: !(formSubmitted && !record?.tally_location_name) ? '1px solid rgba(68, 130, 218, 0.2)' : '1px solid red',
              marginRight: '10px',
            }}
            disabled={connectTenantToTallyLocationLoading}
          >
            {tallyConnectionsFilteredData && tallyConnectionsFilteredData?.filter((connection) => connection?.company_details && connection?.company_details && connection?.locations)?.[0]?.locations
              .map((item) => (
                <Option key={item.tally_location_name} value={item.tally_location_name}>
                  {item?.tally_location_name}
                </Option>
              ))}
          </PRZSelect>
        </div>
      ),
    },
    {
      title: '',
      render: (record) => (
        <div>
          {!record?.tally_facility_codes?.length ? (
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <Popconfirm
                placement="topRight"
                title="Are you sure you want to delete this line?"
                onConfirm={() => removeRow(record?.key)}
                okText="Yes"
                cancelText="No"
              >
                <div className="deleteButton">
                  <DeleteOutlined />
                </div>
              </Popconfirm>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              {' '}
              <Popconfirm
                placement="topRight"
                title="Are you sure you want to delete this line?"
                onConfirm={() => removeRow(record?.key)}
                okText="Yes"
                cancelText="No"
              >
                <div className="deleteButton">
                  {' '}
                  <DeleteOutlined />
                  {' '}
                </div>
              </Popconfirm>

            </div>

          )}

        </div>
      ),

    },
  ];

  const isDataValid = (data) => {
    const copyData = data?.filter((item) => !item?.is_deleted);
    for (let i = 0; i < copyData?.length; i++) {
      if (!data[i]?.tenant_id || !data[i]?.tally_location_name) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = () => {
    setFormSubmitted(true);
    if (isDataValid(tenantData) && tenantData?.filter((item) => !item?.is_deleted && item?.tenant_id && item?.tally_location_name).length > 0) {
      const companyName = tallyConnectionsFilteredData?.find((connection) => connection?.company_details && connection?.company_details && connection?.locations)?.company_details?.name;
      const tenantList = tenantData?.filter((t) => t?.tenant_id && !t?.is_deleted)?.map((tenant) => (
        {
          tenant_id: tenant.tenant_id,
          tally_company_name: tenant?.is_deleted ? null : companyName,
          tally_location_name: tenant?.is_deleted ? null : tenant.tally_location_name,
        }
      ));
      const payload = {
        ...tallyConnectionsFilteredData[0],
        org_id: user?.tenant_info?.org_id,
        tenant: tenantList,
      };

      connectTenantToTallyLocation(payload, () => {
        setStep(stepNumber + 1);
      });
    } else {
      notification.error({
        message: 'Please select location for every tenant',
        placement: 'top',
        duration: 4,
      });
    }
  };

  return (
    <Fragment>
      {(getTenantsLoading || getTallyConnectionsLoading)
        ? (
          <div className="tally-integration__loader__wrapper">

            {[...Array(5).keys()].map((item) => (
              <div key={item} className="tally-integration__loader-row">
                <div className="loadingBlock  tally-integration__loader__line1" />
                <div className="loadingBlock  tally-integration__loader__line2" />
                <div className="loadingBlock  tally-integration__loader__line3" />
              </div>
            ))}
            <div className="loadingBlock tally-integration__loader__line-btn1" />
            <div className="loadingBlock tally-integration__loader__line-btn2" />
          </div>
        )
        : (
          <div>
            <div className="integration-step__wrapper">
              <div className="ant-row">
                <div className="ant-col-md-18">
                  <Table
                    columns={columns}
                    loading={getTenantsLoading || getTallyConnectionsLoading || connectTenantToTallyLocationLoading}
                    dataSource={getDataSource(tenantData) || []}
                    size="small"
                    pagination={false}
                  />
                </div>
              </div>
              <div className="new-row-button" style={{ marginLeft: '10px' }} onClick={() => addNewRow()}>
                <span className="new-row-button__icon"><PlusCircleFilled /></span>
                <div>New Item</div>
              </div>
            </div>

            <div className="integration-step__footer" style={{ display: 'flex', paddingTop: '10px', paddingLeft: '15px' }}>
              <Button
                isLoading={connectTenantToTallyLocationLoading}
                disabled={connectTenantToTallyLocationLoading}
                onClick={() => handleSubmit()}
                type="primary"
              >
                Save and Continue
              </Button>
            </div>
          </div>
        )}
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, TenantReducers, TallyIntegrationReducers }) => ({
  user: UserReducers.user,
  tenants: TenantReducers.tenants,
  tallyConnections: TallyIntegrationReducers.tallyConnections,
  connectTenantToTallyLocationLoading: TallyIntegrationReducers.connectTenantToTallyLocationLoading,
  getTenantsLoading: TenantReducers.getTenantsLoading,
});
const mapDispatchToProps = (dispatch) => ({
  getTenants: (page, keyword, isVerified, orgId) => dispatch(
    TenantActions.getTenants(page, keyword, isVerified, orgId),
  ),
  getTallyConnections: (orgId, callback) => dispatch(TallyIntegrationActions.getTallyConnections(orgId, callback)),
  connectTenantToTallyLocation: (payload, callback) => dispatch(TallyIntegrationActions.connectTenantToTallyLocation(payload, callback)),
});

TallyMapLocations.propTypes = {
  user: PropTypes.any,
  getTenants: PropTypes.func,
  getTallyConnectionsLoading: PropTypes.bool,
  getTenantsLoading: PropTypes.bool,
  tenants: PropTypes.any,
  getTallyConnections: PropTypes.func,
  connectTenantToTallyLocation: PropTypes.func,
  connectTenantToTallyLocationLoading: PropTypes.bool,
  tallyConnections: PropTypes.any,
  setStep: PropTypes.func,
  stepNumber: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TallyMapLocations));
